// Per-SKU (or platform-wide) feature toggle/value table.
//
// Extracted from src/pages/super/licensing/Detail.js on 2026-05-31 — the
// inline table per SKU made the licensing detail page feel cluttered
// (~35 stacked rows on Layerd Dev's 3 SKUs + Platform). Behaviour is
// unchanged from the inline version: bool Switch, numeric inline edit
// with optimistic UI, override-to-default auto-cleanup (DELETE instead
// of redundant PUT), per-row status chip (Default / Override / Platform /
// Expired / Not licensed) and a "Data-plane" tooltip chip for features
// only enforced in pingora's edge binary.
//
// Branches on `skuId === PLATFORM_SKU_KEY` to dispatch through the
// /platform/features endpoints rather than the per-SKU ones — keeps a
// single table component for both code paths so SKU cards and the
// platform card share the same UX in the drawer.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  Switch,
  CircularProgress,
  InputAdornment,
  Tooltip} from "@mui/material";

import { TOKENS, ConfirmModal } from "../design";
import { SafeTextField } from "../../../../common/Greens";
import {
  setFeatureOverride,
  clearFeatureOverride,
  setPlatformFeatureOverride,
  clearPlatformFeatureOverride,
} from "../../../../../services/licensingService";

// Sentinel matching CP `service/admin/licensing.js::PLATFORM_SKU_SENTINEL`.
// Re-exported so callers can `import { PLATFORM_SKU_KEY }` from this
// module instead of cross-referencing the Detail page.
export const PLATFORM_SKU_KEY = "platform";

// Features enforced in pingora's data plane but not validated by any CP
// API surface. The UI surfaces this so super-admins know toggling them
// in CP will not affect any control-plane gate (only the running edge
// binary reads them). TODO: replace with a `pingora_enforced` bool
// column on licensing.features and ship the value via the API.
const DATA_PLANE_ONLY_FEATURES = new Set([
  "api.schema_drift",
  "api.posture_scanning",
  "api.anomaly_engine",
  "api.siem",
  "ai.llm_waf",
  "ai.llm_smart_parse",
  "waf.block_page.custom",
]);

const STATUS_CHIP_VARIANTS = {
  default: { label: "Default", ink: TOKENS.fg2, bg: TOKENS.bgSunken, border: TOKENS.border },
  override: { label: "Override", ink: TOKENS.primaryInk, bg: TOKENS.primaryTint, border: "#CED2F2" },
  platform_default: { label: "Platform", ink: TOKENS.fg2, bg: TOKENS.bgSunken, border: TOKENS.border },
  expired: { label: "Expired", ink: TOKENS.dangerInk, bg: TOKENS.dangerBg, border: TOKENS.dangerBorder },
  not_licensed: { label: "Not licensed", ink: TOKENS.fg2, bg: TOKENS.bgSunken, border: TOKENS.border },
};

const thSx = {
  padding: "10px 14px",
  borderBottom: `1px solid ${TOKENS.border}`,
  background: TOKENS.bgSunken,
  color: TOKENS.fg2,
  fontSize: 11.5,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
const tdSx = {
  padding: "12px 14px",
  borderBottom: `1px solid ${TOKENS.borderSubtle}`,
  fontSize: 13,
  color: TOKENS.fg1,
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch (_) { return String(iso); }
};

export const StatusChip = ({ source }) => {
  const v = STATUS_CHIP_VARIANTS[source] || STATUS_CHIP_VARIANTS.default;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 20,
        px: "8px",
        borderRadius: 999,
        background: v.bg,
        color: v.ink,
        border: `1px solid ${v.border}`,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
      }}
    >
      {v.label}
    </Box>
  );
};

export const DataPlaneChip = () => (
  <Tooltip title="Enforced in pingora; CP API does not validate" arrow>
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: 20,
        px: "8px",
        borderRadius: 999,
        background: TOKENS.infoBg,
        color: TOKENS.infoInk,
        border: `1px solid ${TOKENS.infoBorder}`,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: "0.01em",
        whiteSpace: "nowrap",
        cursor: "help",
      }}
    >
      Data-plane
    </Box>
  </Tooltip>
);

const featureValuesEqual = (a, b) => {
  if (typeof a === "boolean" || typeof b === "boolean") return Boolean(a) === Boolean(b);
  const an = Number(a);
  const bn = Number(b);
  if (Number.isFinite(an) && Number.isFinite(bn)) return an === bn;
  return String(a) === String(b);
};

export const BoolValueCell = ({ row, disabled, onChange }) => {
  const [pending, setPending] = useState(false);
  // Optimistic local mirror — flips immediately, reverts via parent on error.
  const [optimistic, setOptimistic] = useState(null);
  useEffect(() => { setOptimistic(null); }, [row.current_value, row.is_overridden, row.source]);

  const value = optimistic == null ? !!row.current_value : optimistic;

  const handleChange = async (e) => {
    if (disabled || pending) return;
    const next = e.target.checked;
    setOptimistic(next);
    setPending(true);
    try {
      await onChange(next);
    } catch (_) {
      setOptimistic(!next);
    } finally {
      setPending(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Switch
        size="small"
        color="success"
        checked={value}
        disabled={disabled || pending}
        onChange={handleChange}
      />
      {pending ? <CircularProgress size={12} sx={{ color: TOKENS.fg3 }} /> : null}
    </Stack>
  );
};

export const NumberValueCell = ({ row, disabled, onChange }) => {
  const initial = row.current_value == null ? "" : String(row.current_value);
  const [text, setText] = useState(initial);
  const [pending, setPending] = useState(false);
  const lastCommittedRef = useRef(initial);

  // Re-seed when the upstream row updates (e.g. after refetch).
  useEffect(() => {
    setText(initial);
    lastCommittedRef.current = initial;
  }, [initial]);

  const commit = async () => {
    if (pending) return;
    const trimmed = text.trim();
    if (trimmed === "") {
      setText(lastCommittedRef.current);
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n)) {
      setText(lastCommittedRef.current);
      return;
    }
    if (featureValuesEqual(n, lastCommittedRef.current)) return;
    setPending(true);
    try {
      await onChange(n);
      lastCommittedRef.current = String(n);
    } catch (_) {
      setText(lastCommittedRef.current);
    } finally {
      setPending(false);
    }
  };

  return (
    <SafeTextField
      size="small"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
        if (e.key === "Escape") {
          e.preventDefault();
          setText(lastCommittedRef.current);
          e.currentTarget.blur();
        }
      }}
      disabled={disabled || pending}
      inputProps={{
        inputMode: "numeric",
        style: { fontSize: 13, padding: "4px 8px", textAlign: "right", width: 96 },
      }}
      InputProps={{
        endAdornment: pending ? (
          <InputAdornment position="end">
            <CircularProgress size={12} sx={{ color: TOKENS.fg3 }} />
          </InputAdornment>
        ) : row.unit ? (
          <InputAdornment position="end" sx={{ "& .MuiTypography-root": { fontSize: 12, color: TOKENS.fg3 } }}>
            {row.unit}
          </InputAdornment>
        ) : null,
      }}
      sx={{
        "& .MuiOutlinedInput-root": { fontSize: 13 },
      }}
    />
  );
};

export const FeatureTable = ({ skuId, rows, loading, error, onMutated, onError, showHeader = true, showClearAllFooter = true }) => {
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const overrideCount = useMemo(
    () => (rows || []).filter((r) => r.is_overridden).length,
    [rows],
  );

  // Branch on skuId so the table reuses the same UI for SKU-scoped and
  // platform-wide features. Platform features route through dedicated
  // /platform/features/:key endpoints; CP auto-picks the storage SKU.
  const isPlatform = skuId === PLATFORM_SKU_KEY;
  const setFor = (orgId, key, value) =>
    isPlatform
      ? setPlatformFeatureOverride(orgId, key, value)
      : setFeatureOverride(orgId, skuId, key, value);
  const clearFor = (orgId, key) =>
    isPlatform
      ? clearPlatformFeatureOverride(orgId, key)
      : clearFeatureOverride(orgId, skuId, key);

  // Per-feature mutation. If the requested value matches the row's
  // default we DELETE the override instead of PUT-ing a redundant value.
  const mutate = useCallback(async (row, value) => {
    const orgId = row._orgId;
    const matchesDefault = featureValuesEqual(value, row.default_value);
    try {
      if (matchesDefault) {
        // Only fire DELETE if there's actually an override to clear.
        if (row.is_overridden) {
          await clearFor(orgId, row.key);
        }
      } else {
        await setFor(orgId, row.key, value);
      }
      await onMutated?.(skuId);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to update";
      onError?.(`${row.title}: ${msg}`);
      throw e;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuId, onMutated, onError]);

  const handleClearAll = async () => {
    if (clearing) return;
    setClearing(true);
    try {
      const targets = (rows || []).filter((r) => r.is_overridden);
      const orgId = targets[0]?._orgId;
      if (orgId && targets.length > 0) {
        const results = await Promise.allSettled(
          targets.map((r) => clearFor(orgId, r.key)),
        );
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) {
          onError?.(`Failed to clear ${failed} of ${targets.length} overrides.`);
        }
      }
      await onMutated?.(skuId);
    } finally {
      setClearing(false);
      setClearConfirmOpen(false);
    }
  };

  return (
    <Box
      sx={{
        borderTop: showHeader ? `1px solid ${TOKENS.borderSubtle}` : "none",
        background: TOKENS.bgSurface,
      }}
    >
      {showHeader ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: "18px",
            py: "10px",
            borderBottom: `1px solid ${TOKENS.borderSubtle}`,
          }}
        >
          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: TOKENS.fg1 }}>
            Features {rows && rows.length > 0 ? `(${rows.length})` : ""}
          </Typography>
          {overrideCount > 0 ? (
            <Typography sx={{ fontSize: 11.5, color: TOKENS.primaryInk, fontWeight: 600 }}>
              {overrideCount} override{overrideCount === 1 ? "" : "s"}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      {loading && (!rows || rows.length === 0) ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={18} />
        </Box>
      ) : error ? (
        <Typography sx={{ fontSize: 12.5, color: TOKENS.dangerInk, px: 2, py: 1.5 }}>
          {error}
        </Typography>
      ) : !rows || rows.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: TOKENS.fg3, px: 2, py: 1.5, fontStyle: "italic" }}>
          No features registered for this SKU.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>Feature</TableCell>
                <TableCell sx={{ ...thSx, width: 160 }}>Value</TableCell>
                <TableCell sx={{ ...thSx, width: 200 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const isDataPlane = DATA_PLANE_ONLY_FEATURES.has(row.key);
                const disabled = row.source === "not_licensed" || row.source === "expired";
                const overrideMeta = row.is_overridden && row.override_set_by
                  ? `Set by ${row.override_set_by}${row.override_set_at ? ` on ${fmtDate(row.override_set_at)}` : ""}`
                  : null;
                const tipBody = [row.description, overrideMeta].filter(Boolean).join("\n\n") || row.title;
                return (
                  <TableRow key={row.key} hover>
                    <TableCell sx={tdSx}>
                      <Tooltip title={tipBody} arrow placement="top">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>
                            {row.title}
                          </Typography>
                          {row.description ? (
                            <Typography
                              sx={{
                                fontSize: 11.5,
                                color: TOKENS.fg3,
                                mt: 0.25,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 360,
                              }}
                              title={row.description}
                            >
                              {row.description}
                            </Typography>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: TOKENS.fg3,
                                mt: 0.25,
                                fontFamily:
                                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                              }}
                            >
                              {row.key}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      {disabled ? (
                        <Tooltip
                          title={
                            row.source === "expired"
                              ? "SKU expired — value not enforced"
                              : "Org is not licensed for this SKU"
                          }
                          arrow
                        >
                          <Typography component="span" sx={{ fontSize: 13, color: TOKENS.fg3 }}>—</Typography>
                        </Tooltip>
                      ) : row.type === 0 ? (
                        <BoolValueCell
                          row={row}
                          disabled={disabled}
                          onChange={(v) => mutate(row, v)}
                        />
                      ) : (
                        <NumberValueCell
                          row={row}
                          disabled={disabled}
                          onChange={(v) => mutate(row, v)}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                        <StatusChip source={row.source} />
                        {isDataPlane ? <DataPlaneChip /> : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {showClearAllFooter && overrideCount > 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                px: "14px",
                py: "10px",
                borderTop: `1px solid ${TOKENS.borderSubtle}`,
                background: TOKENS.bgSunken,
              }}
            >
              <Button
                onClick={() => setClearConfirmOpen(true)}
                disabled={clearing}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 12.5,
                  color: TOKENS.dangerInk,
                  border: `1px solid ${TOKENS.dangerBorder}`,
                  borderRadius: `${TOKENS.radiusXs}px`,
                  px: 1.5,
                  py: 0.5,
                  "&:hover": { background: TOKENS.dangerBg },
                }}
              >
                Clear all overrides for this SKU
              </Button>
            </Box>
          ) : null}
        </Box>
      )}

      <ConfirmModal
        open={clearConfirmOpen}
        onClose={() => (clearing ? null : setClearConfirmOpen(false))}
        onConfirm={handleClearAll}
        loading={clearing}
        variant="danger"
        title={`Clear ${overrideCount} override${overrideCount === 1 ? "" : "s"}?`}
        body={`This will remove all per-org overrides on this SKU. Features will revert to their SKU or platform defaults.`}
        submitLabel={clearing ? "Clearing…" : "Clear all"}
      />
    </Box>
  );
};

export default FeatureTable;
